import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import { useSelector, useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';

import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import MaximizeIcon from '@mui/icons-material/Maximize';
import RefreshIcon from '@mui/icons-material/Refresh';

import DashboardHeader from './components/DashboardHeader';
import CProjectView from '../../containers/CProjectView/CProjectView';
import MinimizedView from './components/MinimizedView/MinimizedView';
import { fetchLeaksIfNeeded, selectAlert } from '../../actions/AlertsActions';
import {
  fetchOperationalAlerts,
  updateOperationalAlert,
  setCustomerDashboardModalVisibility,
  setCameFromCustomerDashboard,
} from '../../actions/DashboardActions';
import { selectFeature } from '../../actions/selectors';
import { changeAlertsContext } from '../../actions/PrsTransientAlertsActions';

import 'react-resizable/css/styles.css';
import './DashboardModal.scss';
import PopupAlert from '../Notifications/PopupAlert';
import { buildDashboardEnumOptions, capitalizeText } from '../../utils/dashboardEnums';
import { DASHBOARD_GUIDE_TYPES, downloadDashboardGuide } from '../../utils/dashboardGuideDownload';

const DashboardModal = ({ open, onClose }, context) => {
  const dispatch = useDispatch();
  
  // Get all Redux state - must be before any conditional returns
  const user = useSelector(state => state.leaksList.user);
  const selectedProject = useSelector(state => state.leaksList?.selectedProject);
  const projects = useSelector(state => state.leaksList?.projectsList?.items || []);
  const leaksData = useSelector(state => state.leaksList?.leaksByProject?.[selectedProject]);
  const projectFromLeaks = leaksData?.project;
  const reduxSelectedFeature = useSelector(state => state.leaksList?.leaksByProject?.selectedFeature);
  const alertsContext = useSelector(state => state.leaksList?.pressure?.context);
  const options = useSelector(state => state.leaksList?.optionList?.options);
  const userGuides = options?.userGuides;
  const currentLang = useSelector(state => state.i18nState?.lang || 'en');
  // Get leaks array for notifications
  const leaksArray = leaksData?.items || [];
  
  const cameFromCustomerDashboard = useSelector(state => state.leaksList?.dashboard?.cameFromCustomerDashboard);

  // Get operational alerts data from dashboard state
  const operationalAlerts = useSelector(state => state.leaksList?.dashboard?.operationalAlerts);
  const operationalAlertsLoading = operationalAlerts?.loading || false;
  const operationalAlertsArray = Array.isArray(operationalAlerts?.data) ? operationalAlerts.data : [];

  // Helper function to get option label from options
  const getOptionLabel = (optionType, value) => {
    if (!options || !options[optionType] || value == null) return null;

    // Check if this is a dashboard enum with reversed fields
    const isDashboardEnum = optionType.startsWith('dashboard_enum_');

    const option = options[optionType].find(opt => {
      if (isDashboardEnum) {
        // For dashboard enums, 'label' contains the numeric value
        return opt.label == value;
      } else {
        // For other options like AlertState, try standard mapping
        return opt.value == value || opt.code == value;
      }
    });

    if (option) {
      let result;
      if (isDashboardEnum) {
        // For dashboard enums, return the 'value' field which contains the text
        result = option.value;
      } else {
        // For other options, use DisplayName (translated from DB)
        result = option.DisplayName;
      }

      // Capitalize the result for priority and event state
      if (optionType === 'dashboard_enum_priority' || optionType === 'dashboard_enum_opr_state') {
        return capitalizeText(result);
      }

      return result;
    }

    return null;
  };

  // Process notifications for MinimizedView using operational alerts data
  const processNotifications = useCallback(() => {
    // If operational alerts are still loading, return empty array to avoid showing wrong data
    if (operationalAlertsLoading) {
      return [];
    }

    // If operational alerts have loaded but are empty, return empty array
    if (!operationalAlertsArray || operationalAlertsArray.length === 0) {
      return [];
    }

    // Get current project for this processing (not in dependencies to avoid re-renders)
    let currentProject = projects.find(p => p.ID === selectedProject);
    if (!currentProject && projectFromLeaks) {
      currentProject = {
        ID: selectedProject,
        Name: projectFromLeaks.Name || projectFromLeaks.name || context.t('project'),
        ...projectFromLeaks
      };
    }

    // Process operational alerts data directly (no need for leaks data)
    return operationalAlertsArray.map((opAlert) => {
      // Extract fields directly from operational alert
      const alertId = opAlert.alert_id;

      // Map event_priority enum value (0=high, 1=medium, 2=low)
      let priority = '';
      const priorityValue = opAlert.event_priority;
      if (priorityValue != null) {
        priority = getOptionLabel('dashboard_enum_priority', priorityValue);
      }
      // Default to 'High' if priority is empty
      if (!priority) {
        priority = capitalizeText('high');
      }

      // Get event_state text from options
      let eventState = '';
      const eventStateValue = opAlert.event_state;
      if (eventStateValue != null) {
        eventState = getOptionLabel('dashboard_enum_opr_state', eventStateValue);
      }

      // Get event_open_type text from options with fallback
      const openTypeMap = {0: context.t('auto'), 1: context.t('manual'), 2: context.t('import')};
      const openType = getOptionLabel('EventOpenType', opAlert.event_open_type) ||
                      getOptionLabel('OpenType', opAlert.event_open_type) ||
                      getOptionLabel('AlertOpenType', opAlert.event_open_type) ||
                      openTypeMap[opAlert.event_open_type] || context.t('auto');

      // Get event_type text from options
      const eventType = getOptionLabel('EventType', opAlert.event_type) || '';
      
      // Get AlertState text from options (uses DisplayName for translation)
      const alertStatus = getOptionLabel('AlertState', opAlert.alert_state) || context.t('unknown');

      // Probability and comment from operational alerts
      const probability = parseFloat(opAlert.alert_probability || 0);
      const comment = opAlert.alert_comment || '';

      // Indicator logic matching AlertsActions.js
      let indicator = null;

      // Only process indicators for non-closed alerts (matching original logic)
      if (opAlert.alert_state !== 5) { // Not closed
        // Check is_burst field for burst indicator (highest priority)
        if (opAlert.is_burst === 1) {
          indicator = 'Burst';
        }
        // Check LeakGrowthTrend for growth indicators
        else if (opAlert.LeakGrowthTrend) {
          const trend = opAlert.LeakGrowthTrend.toLowerCase();
          if (trend === 'up') {
            indicator = 'Growing Up';
          } else if (trend === 'down') {
            indicator = 'Growing Down';
          }
          // Note: Original sets undefined for other cases, not "No Event"
        }
      }

      // Check is_over field for "No Event" indicator (overrides previous indicators)
      if (currentProject?.is_display_end_event) {
        if (opAlert.is_over
          && (opAlert.event_type == 0 || opAlert.event_type == 3) // Leak or Private Leak
          && [5, 6].indexOf(opAlert.alert_state) === -1 // statuses: [Closed, Fixed]
        ) {
          indicator = 'No Event';
        }
      }

      return {
        id: alertId,
        alertId,
        priority,
        priorityValue: priorityValue != null ? parseInt(priorityValue) : undefined,
        alertStatus,
        probability,
        eventState,
        eventStateValue: eventStateValue != null ? parseInt(eventStateValue) : 0,
        eventType,
        openType,
        comment,
        indicator,
        autoclose: opAlert.autoclose,
        eventCloseDate: opAlert.event_close_date
      };
    });
  }, [operationalAlertsArray, operationalAlertsLoading, options, context.t]);

  const minimizedNotifications = processNotifications();

  // Options for minimized view dropdowns (from server options)
  const eventStateOptions = React.useMemo(() => {
    return buildDashboardEnumOptions(options?.dashboard_enum_opr_state, ['is_opr_alerts', 'is_opr']);
  }, [options]);

  const eventPriorityOptions = React.useMemo(() => {
    return buildDashboardEnumOptions(options?.dashboard_enum_priority);
  }, [options]);

  // Update handlers for minimized view
  const handleMinEventStateChange = (alertId, newState) => {
    if (!alertId) return;
    dispatch(updateOperationalAlert(alertId, { event_state: parseInt(newState) }));
  };

  const handleMinEventPriorityChange = (alertId, newPriority) => {
    if (!alertId) return;
    dispatch(updateOperationalAlert(alertId, { event_priority: parseInt(newPriority) }));
  };

  // Handle refresh of operational alerts
  const handleRefresh = useCallback(() => {
    if (selectedProject) {
      dispatch(fetchOperationalAlerts(selectedProject));
    }
  }, [selectedProject, dispatch]);

  const handleHelpDownload = useCallback(() => {
    downloadDashboardGuide({
      dashboardType: DASHBOARD_GUIDE_TYPES.PROJECT,
      dispatch,
      projectId: selectedProject,
      currentLang,
      expectedGuideName: context.t('project_dashboard'),
      userGuides,
    });
  }, [dispatch, selectedProject, currentLang, userGuides, context.t]);

  // Modal state
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [minimizedPosition, setMinimizedPosition] = useState(null);
  // Width fills most of viewport, height is fit-content (determined by CSS)
  const initialWidth = Math.round(window.innerWidth * 0.95);
  const [position, setPosition] = useState({
    x: Math.max(0, (window.innerWidth - initialWidth) / 2),
    y: 20
  });
  const [size, setSize] = useState({ width: initialWidth, height: 'auto' });
  const [highlightedAlertId, setHighlightedAlertId] = useState(null);

  // Shared filter state for both ProjectView and MinimizedView
  const [sharedPriorityFilter, setSharedPriorityFilter] = useState([]);
  const [sharedEventStateFilter, setSharedEventStateFilter] = useState([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [popupAlert, setPopupAlert] = useState({
    popupKey: -1,
    message: '',
    severity: 'info',
    action: null,
  });

  // Callback to update filters from ProjectView/NotificationSection
  const handleFiltersChange = React.useCallback((priorityFilter, eventStateFilter) => {
    setSharedPriorityFilter(priorityFilter);
    setSharedEventStateFilter(eventStateFilter);
    setFiltersInitialized(true);
  }, []);

  // Reset size and position when modal opens - width 95%, centered horizontally, near top
  useEffect(() => {
    if (open && !isMinimized) {
      const desiredWidth = Math.round(window.innerWidth * 0.95);
      setSize({ width: desiredWidth, height: 'auto' });
      setPosition({
        x: Math.max(0, (window.innerWidth - desiredWidth) / 2),
        y: 20 // Fixed offset from top since height is fit-content
      });
      // Reset popup alert to prevent stale notifications from appearing
      setPopupAlert({
        popupKey: -1,
        message: '',
        severity: 'info',
        action: null,
      });
    }
  }, [open, isMinimized]);
  
  // Fetch data when modal opens or project changes
  useEffect(() => {
    if (open && selectedProject) {
      dispatch(fetchLeaksIfNeeded(selectedProject));
      dispatch(fetchOperationalAlerts(selectedProject));
      // Reset popup alert when project changes to prevent stale notifications
      setPopupAlert({
        popupKey: -1,
        message: '',
        severity: 'info',
        action: null,
      });
    }
  }, [open, selectedProject, dispatch]);

  // Auto-close minimized view when project changes
  const prevProjectRef = useRef(selectedProject);
  useEffect(() => {
    // Only close if minimized AND project actually changed (not initial load)
    if (isMinimized && prevProjectRef.current && prevProjectRef.current !== selectedProject) {
      // Reset minimized state before closing so it opens in full view next time
      setIsMinimized(false);
      setMinimizedPosition(null);
      // Close the entire modal when project changes to avoid showing irrelevant data
      onClose();
    }
    prevProjectRef.current = selectedProject;
  }, [selectedProject, isMinimized, onClose]);

  // Listen for focusAlert events to highlight alerts
  useEffect(() => {
    const handleFocusAlert = (event) => {
      const alertId = event.detail?.alertId;
      if (alertId) {
        setHighlightedAlertId(alertId);
      }
    };

    window.addEventListener('focusAlert', handleFocusAlert);
    return () => window.removeEventListener('focusAlert', handleFocusAlert);
  }, []);

  // Sync highlighted alert in minimized view when alert is selected from map or main table
  useEffect(() => {
    if (!open || !isMinimized || !reduxSelectedFeature) return;

    const existsInMinimized = minimizedNotifications.some(
      n => n.alertId == reduxSelectedFeature || n.id == reduxSelectedFeature
    );

    setHighlightedAlertId(existsInMinimized ? reduxSelectedFeature : null);
  }, [reduxSelectedFeature, open, isMinimized, minimizedNotifications]);

  // Recenter horizontally on window resize when relevant
  useEffect(() => {
    const handleWindowResize = () => {
      if (open && !isMinimized && !isMaximized) {
        const newWidth = Math.round(window.innerWidth * 0.95);
        setSize({ width: newWidth, height: 'auto' });
        setPosition({
          x: Math.max(0, (window.innerWidth - newWidth) / 2),
          y: 20
        });
      }
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [open, isMinimized, isMaximized]);

  // Handle delayed map element loading for minimized positioning
  useEffect(() => {
    if (isMinimized && !minimizedPosition) {
      // Try to find map element after a short delay if position not set
      const timer = setTimeout(() => {
        const mapContainer = document.querySelector('.panel-map') ||
                           document.querySelector('.map-wrapper') ||
                           document.querySelector('.map');

        if (mapContainer && !minimizedPosition) {
          // Force a re-render to recalculate position if map is now available
          setMinimizedPosition(null);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isMinimized, minimizedPosition]);

  // Handle ESC key to close the modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [open, onClose]);
  
  const notify = (message, level, action) => {
    setPopupAlert({
      popupKey: new Date().getTime(),
      message: message,
      severity: level,
      action: action,
    });
  }

  // Handle drag
  const handleDrag = (_, data) => {
    if (!isMaximized) {
      setPosition({ x: data.x, y: data.y });
    }
  };
  
  
  // Handle resize - only allow width resizing since height is fit-content
  const handleResize = (_, { size: newSize }) => {
    if (!isMaximized && !isMinimized) {
      setSize({ width: newSize.width, height: 'auto' });
    }
  };
  
  // Toggle functions
  const toggleMinimize = () => {
    // When minimizing, reset position to ensure it's calculated based on current map position
    if (!isMinimized) {
      setMinimizedPosition(null); // Reset position to trigger recalculation
    }
    setIsMinimized(!isMinimized);
    setIsMaximized(false);
  };
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  };
  
  // Handle alert click
  const handleAlertClick = (alertId) => {
    const currentPath = window.location.pathname;
    const isOnAlertsPage = currentPath === '/alerts';

    // Set highlighted alert
    setHighlightedAlertId(alertId);

    // Find the leak object to get the full data
    const leak = leaksArray.find(l => l.id === alertId || l.ID === alertId);
    
    // Dispatch selectFeature to set this as the selected alert
    if (selectedProject && alertId && leak) {
      // Use the actual ID field from the leak object (could be 'id' or 'ID')
      const leakId = leak.ID || leak.id;
      dispatch(selectFeature(selectedProject, leakId, 'leak', leak));
      dispatch(selectAlert(leak));
    }
    
    // If dashboard is in minimized mode, keep it open and floating
    if (isMinimized) {
      if (!isOnAlertsPage || alertsContext === 'noise' || alertsContext === 'valve') {
        // Switch to regular alerts context if in noise or valve
        if (alertsContext === 'noise' || alertsContext === 'valve') {
          dispatch(changeAlertsContext('alerts'));
        }
        
        // Navigate using React Router
        browserHistory.push('/alerts');
        
        // Wait a bit for navigation and data to load, then dispatch focus event
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('focusAlert', { 
            detail: { 
              alertId: leak?.ID || leak?.id || alertId,
              keepDashboardOpen: true 
            } 
          }));
        }, 1000);
      } else {
        // Already on alerts page, dispatch focus event
        window.dispatchEvent(new CustomEvent('focusAlert', { 
          detail: { 
            alertId: leak?.ID || leak?.id || alertId,
            keepDashboardOpen: true 
          } 
        }));
      }
    } else {
      // In full-size mode - minimize the dashboard instead of closing
      setMinimizedPosition(null); // Reset position to trigger recalculation
      setIsMinimized(true);
      setIsMaximized(false);
      
      if (!isOnAlertsPage || alertsContext === 'noise' || alertsContext === 'valve') {
        // Switch to regular alerts context if in noise or valve
        if (alertsContext === 'noise' || alertsContext === 'valve') {
          dispatch(changeAlertsContext('alerts'));
        }
        
        // Navigate using React Router
        browserHistory.push('/alerts');
        
        // Wait a bit for navigation and data to load, then dispatch focus event
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('focusAlert', { 
            detail: { 
              alertId: leak?.ID || leak?.id || alertId,
              keepDashboardOpen: true 
            } 
          }));
        }, 1000);
      } else {
        // Already on alerts page - dispatch focus event
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('focusAlert', { 
            detail: { 
              alertId,
              keepDashboardOpen: true 
            } 
          }));
        }, 100);
      }
    }
  };


  // Get current project data - try multiple sources
  const currentProject = projects.find(p => p.ID === selectedProject) ||
    (projectFromLeaks && {
      ID: selectedProject,
      Name: projectFromLeaks.Name || projectFromLeaks.name || context.t('project'),
      ...projectFromLeaks
    }) ||
    (selectedProject && {
      ID: selectedProject,
      Name: `Project ${selectedProject.substring(0, 8)}...`
    });

  if (!open) return null;

  // Block access for non-AQS users unless they have dashboard permission  
  if (!user.isAQS && !user.dashboard) {
    return null;
  }
  
  // Minimized view
  if (isMinimized) {
    // Calculate position based on map element for consistency across screen sizes
    const calculateMinimizedPosition = () => {
      // Try to find the map container element first
      const mapContainer = document.querySelector('.panel-map') ||
                          document.querySelector('.map-wrapper') ||
                          document.querySelector('.map');

      if (mapContainer) {
        // Position based on actual map element bounds
        const mapBounds = mapContainer.getBoundingClientRect();

        // Position modal with consistent offset from map's left edge
        // This ensures same relative position regardless of screen size
        const leftOffset = 50; // Fixed offset from map's left edge
        const topOffset = 100; // Fixed offset from top

        return {
          x: Math.max(10, mapBounds.left + leftOffset), // Ensure minimum 10px from window edge
          y: topOffset
        };
      } else {
        // Fallback: use alerts table as reference if map not found
        const alertsTable = document.querySelector('.alerts-wrp');

        if (alertsTable) {
          const tableRight = alertsTable.getBoundingClientRect().right;
          // Position modal just to the right of the alerts table with fixed offset
          return {
            x: Math.max(10, tableRight + 50), // 50px offset from table edge
            y: 100
          };
        } else {
          // Final fallback: position based on viewport percentages
          // This maintains consistency by using fixed percentages
          return {
            x: window.innerWidth * 0.45, // Position at 45% of screen width
            y: 100
          };
        }
      }
    };

    // Calculate default position only if not already set
    const defaultPosition = minimizedPosition || calculateMinimizedPosition();

    return (
      <Draggable
        handle=".dashboard-minimized-header"
        position={minimizedPosition || defaultPosition}
        onStop={(_, data) => {
          setMinimizedPosition({ x: data.x, y: data.y });
        }}
      >
        <div className="dashboard-modal-minimized-container">
          <Paper className="dashboard-modal-minimized">
            <div className="dashboard-minimized-header">
              <span className="title">{context.t('high_priority_leak_alerts')}</span>
              <div className="header-actions">
                <IconButton size="small" onClick={handleRefresh} title={context.t('refresh')}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={toggleMinimize}>
                  <MaximizeIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
            <MinimizedView
              notifications={minimizedNotifications}
              onAlertClick={handleAlertClick}
              highlightedAlertId={highlightedAlertId}
              onEventStateChange={handleMinEventStateChange}
              onEventPriorityChange={handleMinEventPriorityChange}
              eventStateOptions={eventStateOptions}
              eventPriorityOptions={eventPriorityOptions}
              selectedPriorityFilter={sharedPriorityFilter}
              selectedEventStateFilter={sharedEventStateFilter}
            />
          </Paper>
        </div>
      </Draggable>
    );
  }
  
  // Full/Normal view - width controlled by size, height fits content
  const modalStyle = isMaximized
    ? { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', maxWidth: '100vw' }
    : { width: size.width };
    
  const content = (
    <Paper className={`dashboard-modal ${isMaximized ? 'maximized' : ''}`} style={modalStyle}>
      <DashboardHeader
        onClose={onClose}
        onMinimize={toggleMinimize}
        onMaximize={toggleMaximize}
        isMaximized={isMaximized}
        projectName={currentProject?.Name}
        t={context.t}
        onHelp={handleHelpDownload}
        onBackToCustomer={cameFromCustomerDashboard ? () => {
          dispatch(setCameFromCustomerDashboard(false));
          onClose();
          dispatch(setCustomerDashboardModalVisibility(true));
        } : undefined}
      />
      <div className="dashboard-modal-content">
        <CProjectView
          notify={notify}
          project={currentProject}
          onAlertClick={handleAlertClick}
          onFiltersChange={handleFiltersChange}
          sharedPriorityFilter={sharedPriorityFilter}
          sharedEventStateFilter={sharedEventStateFilter}
          filtersInitialized={filtersInitialized}
        />
      </div>
      <PopupAlert  {...popupAlert} />
    </Paper>
  );
  
  if (isMaximized) {
    return content;
  }
  
  return (
    <div className="dashboard-modal-overlay">
      <Draggable
        handle=".dashboard-header"
        position={position}
        onDrag={handleDrag}
        bounds="parent"
      >
        <Resizable
          width={size.width}
          height={window.innerHeight * 0.9} // Placeholder for Resizable, actual height is fit-content via CSS
          onResize={handleResize}
          minConstraints={[Math.round(window.innerWidth * 0.5), 200]}
          maxConstraints={[Math.round(window.innerWidth * 0.98), window.innerHeight * 0.98]}
          resizeHandles={['e', 'w']} // Only allow horizontal resizing
        >
          {content}
        </Resizable>
      </Draggable>
    </div>
  );
};

DashboardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

DashboardModal.contextTypes = {
  t: PropTypes.func.isRequired
};

export default DashboardModal;
